import { lazy, Suspense, useCallback, useEffect, useState } from 'react';

import { useUnit } from 'effector-react';
import { Switch, Route, useHistory, useLocation } from 'react-router-dom';

import AppAlert from '@m/components/AppAlert';
import { useHeapIdentifyUser } from '@m/hooks/useHeapIdentifyUser';
import { Wrapper } from '@m/legacy-auth/components/Layout';
import LoginPromptContainer from '@m/legacy-auth/containers/LoginPromptContainer';
import { useIsAuthed } from '@m/legacy-auth/hooks/useIsAuthed';
import { getUser, logout, userStore } from '@m/legacy-auth/state/user';
import { hasIntakePermissions } from '@m/utils/intake/hasIntakePermission';

const IntakeContainer = lazy(() => import('apps/intake/containers/IntakeContainer'));
const NavbarContainer = lazy(() => import('apps/specialist/containers/NavbarContainer'));
const IntakeCaptureModal = lazy(() => import('apps/intake/containers/IntakeCaptureModal'));

export default function Intake() {
  const history = useHistory();
  const { pathname } = useLocation();
  const { user } = useUnit(userStore);

  const isAuthed = useIsAuthed();

  const [modalVisible, setModalVisible] = useState(false);

  const openModal = useCallback(() => setModalVisible(true), []);
  const closeModal = useCallback(() => setModalVisible(false), []);

  // initial fetch of user data
  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    async function handleInvalidPermissions() {
      await logout();
      history.replace('/login');
    }
    if (user && !hasIntakePermissions(user.effectivePermissions)) {
      handleInvalidPermissions();
    } else if (isAuthed === false && pathname !== '/login') {
      history.replace('/login');
    }
  }, [isAuthed, pathname, history, user]);

  useHeapIdentifyUser({
    authenticated: isAuthed,
    user,
  });

  return (
    <>
      <AppAlert />
      <Wrapper>
        <Suspense fallback={null}>{isAuthed && <NavbarContainer />}</Suspense>
        <Switch>
          <Route exact path="/">
            <Suspense fallback={null}>
              {isAuthed && (
                <>
                  <IntakeContainer onBeginCapture={openModal} />
                  <IntakeCaptureModal visible={modalVisible} onClose={closeModal} />
                </>
              )}
            </Suspense>
          </Route>
          <Route exact path="/login">
            <LoginPromptContainer disableSSOAuth />
          </Route>
        </Switch>
      </Wrapper>
    </>
  );
}
